/**
 * Site access gate — the shared doorway passphrase in front of the whole
 * deployment (DPO UAT). Enforced at the edge by nginx; this module only
 * reacts to being turned away.
 *
 * Why the SPA has to participate at all: the app is a PWA, so a returning
 * browser answers navigations from the service-worker precache and never asks
 * nginx for the page. A gate on page loads alone is therefore invisible to
 * exactly the people it is supposed to stop — it only ever challenged
 * first-time visitors and crawlers. The gate that bites is the one on `/api/`,
 * and that one surfaces as a failed data call rather than a browser prompt. So
 * when the edge marks a response as gate-rejected, we navigate to the unlock
 * page ourselves.
 *
 * Own module rather than part of `api.ts` because `auth.ts` needs it too, and
 * api → auth already runs one way.
 */

/** Set by nginx when a request was turned away by the gate, not the API. */
export const SITE_GATE_HEADER = "X-Site-Gate";

/** The unlock page. Served outside the gate and excluded from the service
 *  worker precache, so this navigation always reaches the real server. */
export const GATE_PATH = "/gate.html";

/** True when this response is the gate talking, not Django. */
export function isSiteGateRejection(response: Response): boolean {
  return response.headers.get(SITE_GATE_HEADER) === "required";
}

/** Send the user to the unlock page, remembering where they were.
 *
 *  `replace` keeps the dead state out of history; the guard stops a loop if
 *  the gate ever answers for its own page. */
export function redirectToSiteGate(): void {
  if (typeof window === "undefined") return;
  if (window.location.pathname === GATE_PATH) return;
  const next = window.location.pathname + window.location.search;
  window.location.replace(`${GATE_PATH}?next=${encodeURIComponent(next)}`);
}
